import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import {
  normalizeChoiceOptions,
  type ProcedureAuthoringDraft,
  type ProcedureGroup,
  type ProcedureItem,
} from "../components/procedures/procedureAuthoring";

const responseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  owner: z.string(),
  ownerId: z.number().int().positive(),
  category: z.string().nullable(),
  categories: z.array(z.string()).default([]),
  categoryIds: z.array(z.number().int().positive()).default([]),
  team: z.string().nullable(),
  teamId: z.number().int().positive().nullable(),
  version: z.number().int().positive(),
  sections: z.array(
    z.object({
      id: z.string().uuid(),
      parentSectionId: z.string().uuid().nullable(),
      title: z.string(),
      order: z.number(),
    }),
  ),
  items: z.array(
    z.object({
      id: z.string().uuid(),
      sectionId: z.string().uuid().nullable(),
      parentItemId: z.string().uuid().nullable(),
      label: z.string(),
      description: z.string().nullable(),
      required: z.boolean(),
      type: z.enum(["TEXT", "CHECKBOX", "NUMERIC", "SINGLE_CHOICE", "PHOTO"]),
      order: z.number(),
      unit: z.string().optional(),
      options: z.array(z.string()).optional(),
    }),
  ),
});
const categoryListSchema = z.array(
  z.object({ id: z.number().int().positive(), name: z.string().min(1) }),
);
export type AuthoringProcedure = z.infer<typeof responseSchema>;
export type ProcedureCategoryOption = z.infer<
  typeof categoryListSchema
>[number];

function basePath(customerId: string, facilityId: string) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}`;
}

async function parseResponse(response: Response) {
  const body = await response.text();
  let payload: unknown = null;
  if (body.trim()) {
    try {
      payload = JSON.parse(body) as unknown;
    } catch {
      throw new Error(
        `The server returned an invalid response (${response.status}).`,
      );
    }
  }
  if (!response.ok) {
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  if (payload === null) {
    throw new Error(
      `The save request returned an empty response (${response.status}).`,
    );
  }
  return responseSchema.parse(payload);
}

function canonicalType(
  type: ProcedureItem["type"],
): AuthoringProcedure["items"][number]["type"] {
  return (
    {
      text: "TEXT",
      checkbox: "CHECKBOX",
      number: "NUMERIC",
      choice: "SINGLE_CHOICE",
      photo: "PHOTO",
    } as const
  )[type];
}
function localType(
  type: AuthoringProcedure["items"][number]["type"],
): ProcedureItem["type"] {
  return (
    {
      TEXT: "text",
      CHECKBOX: "checkbox",
      NUMERIC: "number",
      SINGLE_CHOICE: "choice",
      PHOTO: "photo",
    } as const
  )[type];
}

function toCanonical(draft: ProcedureAuthoringDraft) {
  // Written in the order the editor shows: loose sections, then headings with
  // their sections, then loose steps. Root order numbers follow that order
  // too. Readers still order through the shared helper rather than trusting
  // this layout.
  const sections = draft.looseSections
    .map((section, sectionIndex): AuthoringProcedure["sections"][number] => ({
      id: section.id,
      parentSectionId: null,
      title: section.title.trim() || "Untitled section",
      order: sectionIndex + 1,
    }))
    .concat(
      draft.groups.flatMap((group, groupIndex) => [
        {
          id: group.id,
          parentSectionId: null,
          title: group.title.trim() || "Untitled heading",
          order: draft.looseSections.length + groupIndex + 1,
        },
        ...group.sections.map((section, sectionIndex) => ({
          id: section.id,
          parentSectionId: group.id,
          title: section.title.trim() || "Untitled section",
          order: sectionIndex + 1,
        })),
      ]),
    );
  const items = [
    ...draft.looseSections.flatMap((section) =>
      section.items.map((item, index) => ({
        ...item,
        sectionId: section.id,
        order: index + 1,
      })),
    ),
    ...draft.groups.flatMap((group) =>
      group.sections.flatMap((section) =>
        section.items.map((item, index) => ({
          ...item,
          sectionId: section.id,
          order: index + 1,
        })),
      ),
    ),
    ...draft.looseItems.map((item, index) => ({
      ...item,
      sectionId: null,
      order: index + 1,
    })),
  ].map((item) => ({
    id: item.id,
    sectionId: item.sectionId,
    parentItemId: null,
    label: item.label.trim(),
    description: item.description.trim() || null,
    required: item.required,
    type: canonicalType(item.type),
    order: item.order,
    ...(item.type === "number" && item.unit.trim()
      ? { unit: item.unit.trim() }
      : {}),
    ...(item.type === "choice"
      ? { options: normalizeChoiceOptions(item.options) }
      : {}),
  }));
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    owner: draft.owner.trim(),
    ownerId: draft.ownerId,
    category: draft.category.trim() || null,
    categories: draft.categories,
    categoryIds: draft.categoryIds,
    team: draft.team.trim() || null,
    teamId: draft.teamId,
    sections,
    items,
  };
}

function fromCanonical(procedure: AuthoringProcedure): ProcedureAuthoringDraft {
  const rootSections = procedure.sections.filter(
    (section) => section.parentSectionId === null,
  );
  const groups: ProcedureGroup[] = rootSections
    .filter((section) =>
      procedure.sections.some(
        (candidate) => candidate.parentSectionId === section.id,
      ),
    )
    .map((group) => ({
      id: group.id,
      title: group.title,
      sections: procedure.sections
        .filter((section) => section.parentSectionId === group.id)
        .sort((a, b) => a.order - b.order)
        .map((section) => ({
          id: section.id,
          title: section.title,
          items: procedure.items
            .filter((item) => item.sectionId === section.id)
            .sort((a, b) => a.order - b.order)
            .map((item) => ({
              id: item.id,
              label: item.label,
              description: item.description ?? "",
              type: localType(item.type),
              required: item.required,
              options: item.options ?? [],
              unit: item.unit ?? "",
            })),
        })),
    }));
  const looseSections = rootSections
    .filter(
      (section) =>
        !procedure.sections.some(
          (candidate) => candidate.parentSectionId === section.id,
        ),
    )
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      id: section.id,
      title: section.title,
      items: procedure.items
        .filter((item) => item.sectionId === section.id)
        .sort((a, b) => a.order - b.order)
        .map((item) => ({
          id: item.id,
          label: item.label,
          description: item.description ?? "",
          type: localType(item.type),
          required: item.required,
          options: item.options ?? [],
          unit: item.unit ?? "",
        })),
    }));
  const items = procedure.items
    .filter((item) => item.sectionId === null)
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      id: item.id,
      label: item.label,
      description: item.description ?? "",
      type: localType(item.type),
      required: item.required,
      options: item.options ?? [],
      unit: item.unit ?? "",
    }));
  return {
    id: procedure.id,
    title: procedure.title,
    description: procedure.description ?? "",
    category: procedure.category ?? "",
    categories: procedure.categories.length
      ? procedure.categories
      : procedure.category
        ? [procedure.category]
        : [],
    categoryIds: procedure.categoryIds,
    owner: procedure.owner,
    ownerId: procedure.ownerId,
    team: procedure.team ?? "",
    teamId: procedure.teamId,
    groups,
    looseSections,
    looseItems: items,
  };
}

export async function fetchManualProcedure(
  customerId: string,
  facilityId: string,
  procedureId: string,
) {
  return fromCanonical(
    await parseResponse(
      await workApiFetch(
        `${basePath(customerId, facilityId)}/procedures/${encodeURIComponent(procedureId)}/authoring`,
      ),
    ),
  );
}

export async function fetchProcedureCategories(
  customerId: string,
  facilityId: string,
) {
  const response = await workApiFetch(
    `${basePath(customerId, facilityId)}/procedures/authoring/categories`,
  );
  const payload: unknown = await response.json();
  if (!response.ok) {
    const message =
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return categoryListSchema.parse(payload);
}
export async function saveProcedureAuthoring(
  customerId: string,
  facilityId: string,
  procedureId: string | undefined,
  draft: ProcedureAuthoringDraft,
) {
  const response = await workApiFetch(
    procedureId
      ? `${basePath(customerId, facilityId)}/procedures/${encodeURIComponent(procedureId)}/authoring`
      : `${basePath(customerId, facilityId)}/procedures/authoring`,
    {
      method: procedureId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toCanonical(draft)),
    },
  );
  return parseResponse(response);
}
